import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { sourcesApi, citationsApi } from '@/api';
import type {
  CitationLinkResponse,
  CitationLinkTargetType,
  CitationResponse,
  SourceResponse
} from 'we-tree-api-client/v2';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';

import './citation-links-field.css';

/** Target types supported by this field (facts and life events only). */
export type FactOrLifeEventTargetType = Extract<CitationLinkTargetType, 'person_fact' | 'life_event'>;

const CREATE_NEW_VALUE = '__new__';
const CONFIDENCE_OPTIONS = ['low', 'medium', 'high'] as const;

interface CitationLinksFieldProps {
  familyId: string;
  targetType: FactOrLifeEventTargetType;
  targetId: string;
  readonly?: boolean;
}

/**
 * Lists citation links for a fact or life event, and allows adding/removing links (when not readonly).
 * Add flow: select a Source first, then either pick an existing Citation for that source or create a new one, then link.
 */
const CitationLinksField: React.FC<CitationLinksFieldProps> = observer(
  ({ familyId, targetType, targetId, readonly = false }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const [links, setLinks] = useState<CitationLinkResponse[]>([]);
    const [citationDetails, setCitationDetails] = useState<Record<string, CitationResponse>>({});
    const [sources, setSources] = useState<SourceResponse[]>([]);
    const [selectedSourceId, setSelectedSourceId] = useState('');
    const [citationsForSource, setCitationsForSource] = useState<CitationResponse[]>([]);
    const [addCitationId, setAddCitationId] = useState('');
    const [newDetailLocator, setNewDetailLocator] = useState('');
    const [newNotes, setNewNotes] = useState('');
    const [newConfidence, setNewConfidence] = useState<(typeof CONFIDENCE_OPTIONS)[number] | ''>('');
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [loadingCitations, setLoadingCitations] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageOverlayMediaId, setImageOverlayMediaId] = useState<string | null>(null);

    const loadLinks = useCallback(async () => {
      setLoadingLinks(true);
      setError(null);
      const result = await citationsApi.listCitationLinksByTarget(familyId, targetType, targetId);
      setLoadingLinks(false);
      if (result.success && result.data) {
        setLinks(result.data.items);
      } else {
        setLinks([]);
      }
    }, [familyId, targetType, targetId]);

    const loadSources = useCallback(async () => {
      setLoadingSources(true);
      const result = await sourcesApi.listSources(familyId, { pageSize: 100 });
      setLoadingSources(false);
      if (result.success && result.data) {
        setSources(result.data.items);
      } else {
        setSources([]);
      }
    }, [familyId]);

    const loadCitationsForSource = useCallback(
      async (sourceId: string) => {
        setLoadingCitations(true);
        const result = await citationsApi.listCitations(familyId, {
          sourceId,
          pageSize: 100
        });
        setLoadingCitations(false);
        if (result.success && result.data) {
          setCitationsForSource(result.data.items);
        } else {
          setCitationsForSource([]);
        }
      },
      [familyId]
    );

    useEffect(() => {
      loadLinks();
    }, [loadLinks]);

    useEffect(() => {
      if (!readonly) {
        loadSources();
      } else if (links.length > 0) {
        loadSources();
      }
    }, [readonly, links.length, loadSources]);

    useEffect(() => {
      if (selectedSourceId) {
        loadCitationsForSource(selectedSourceId);
      } else {
        setCitationsForSource([]);
        setAddCitationId('');
      }
    }, [selectedSourceId, loadCitationsForSource]);

    // Fetch citation details for each link so we can show source + detailLocator/notes
    useEffect(() => {
      if (links.length === 0) {
        setCitationDetails({});
        return;
      }
      let cancelled = false;
      const fetchDetails = async () => {
        const results = await Promise.all(links.map((link) => citationsApi.getCitation(familyId, link.citationId)));
        if (cancelled) return;
        const details: Record<string, CitationResponse> = {};
        results.forEach((result, i) => {
          if (result.success && result.data) {
            details[links[i].citationId] = result.data;
          }
        });
        setCitationDetails(details);
      };
      fetchDetails();
      return () => {
        cancelled = true;
      };
    }, [familyId, links]);

    const sourceById = useCallback((id: string) => sources.find((s) => s.id === id), [sources]);

    const handleLinkExistingCitation = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!addCitationId.trim() || addCitationId === CREATE_NEW_VALUE) return;
      setSubmitting(true);
      setError(null);
      const result = await citationsApi.createCitationLink(familyId, addCitationId.trim(), {
        targetType,
        targetId,
        supports: true
      });
      setSubmitting(false);
      if (result.success) {
        setAddCitationId('');
        await loadLinks();
      } else {
        setError(result.error?.message ?? t('citations.errors.linkFailed'));
      }
    };

    const handleCreateAndLinkCitation = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedSourceId) return;
      setSubmitting(true);
      setError(null);
      const createResult = await sourcesApi.createCitation(familyId, selectedSourceId, {
        detailLocator: newDetailLocator.trim() || undefined,
        notes: newNotes.trim() || undefined,
        confidence: newConfidence || undefined
      });
      if (!createResult.success || !createResult.data) {
        setSubmitting(false);
        setError(
          !createResult.success && 'error' in createResult
            ? (createResult.error?.message ?? t('citations.errors.createFailed'))
            : t('citations.errors.createFailed')
        );
        return;
      }
      const linkResult = await citationsApi.createCitationLink(familyId, createResult.data.id, {
        targetType,
        targetId,
        supports: true
      });
      setSubmitting(false);
      if (linkResult.success) {
        setNewDetailLocator('');
        setNewNotes('');
        setNewConfidence('');
        setAddCitationId('');
        await loadCitationsForSource(selectedSourceId);
        await loadLinks();
      } else {
        setError(linkResult.error?.message ?? t('citations.errors.linkFailed'));
      }
    };

    const handleRemoveLink = async (citationId: string, linkId: string) => {
      setSubmitting(true);
      setError(null);
      const result = await citationsApi.deleteCitationLink(familyId, citationId, linkId);
      setSubmitting(false);
      if (result.success) {
        await loadLinks();
      } else {
        setError(result.error?.message ?? t('citations.errors.removeFailed'));
      }
    };

    const handleViewSourceImage = (mediaAssetId: string) => {
      setImageOverlayMediaId(mediaAssetId);
    };

    const linkedCitationIds = new Set(links.map((l) => l.citationId));
    const availableCitations = citationsForSource.filter((c) => !linkedCitationIds.has(c.id));
    const showCreateForm = addCitationId === CREATE_NEW_VALUE;

    const citationLabel = (c: CitationResponse): string => {
      const part = c.detailLocator || c.notes || '';
      return part ? `${part.slice(0, 60)}${part.length > 60 ? '…' : ''}` : t('citations.citationFallback');
    };

    const linkedItemLabel = (link: CitationLinkResponse): string => {
      const detail = citationDetails[link.citationId];
      if (!detail) return t('citations.citationFallback');
      const source = sourceById(detail.sourceId);
      const sourceTitle = source?.title ?? t('citations.sourceFallback');
      const citePart = citationLabel(detail);
      return citePart ? `${sourceTitle} — ${citePart}` : sourceTitle;
    };

    const linkedItemMediaAssetId = (link: CitationLinkResponse): string | undefined =>
      (citationDetails[link.citationId] as (CitationResponse & { mediaAssetId?: string }) | undefined)?.mediaAssetId;

    const hasAnySources = sources.length > 0;
    return (
      <div className="citation-links-field">
        <label className="citation-links-field__label">{t('citations.title')}</label>
        {error && (
          <div className="citation-links-field__error" role="alert">
            {error}
          </div>
        )}
        {loadingLinks ? (
          <p className="citation-links-field__loading">{t('citations.loading')}</p>
        ) : links.length === 0 && !readonly && !hasAnySources ? (
          <p className="citation-links-field__hint">{t('citations.emptyHint')}</p>
        ) : (
          <>
            {links.length > 0 && (
              <ul className="citation-links-field__list" aria-label={t('citations.linked')}>
                {links.map((link) => {
                  const mediaAssetId = linkedItemMediaAssetId(link);
                  return (
                    <li key={link.id} className="citation-links-field__item">
                      <span className="citation-links-field__item-label">{linkedItemLabel(link)}</span>
                      <span className="citation-links-field__item-actions">
                        {mediaAssetId && (
                          <button
                            type="button"
                            className="citation-links-field__view-image"
                            onClick={() => handleViewSourceImage(mediaAssetId)}
                            aria-label={t('citations.viewSourceImage')}
                          >
                            {t('citations.viewImage')}
                          </button>
                        )}
                        {!readonly && (
                          <button
                            type="button"
                            className="citation-links-field__remove"
                            onClick={() => handleRemoveLink(link.citationId, link.id)}
                            disabled={submitting}
                            aria-label={t('citations.removeLink')}
                          >
                            {tCommon('remove')}
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {!readonly && hasAnySources && (
              <div className="citation-links-field__add">
                <div className="citation-links-field__add-row">
                  <Select.Root
                    value={selectedSourceId || undefined}
                    onValueChange={(v) => {
                      setSelectedSourceId(v || '');
                      setAddCitationId('');
                    }}
                    disabled={loadingSources || submitting}
                  >
                    <Select.Trigger className="form-select-trigger" aria-label={t('citations.selectSource')}>
                      <Select.Value placeholder={t('citations.selectSourcePlaceholder')} />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                        {sources.map((s) => (
                          <Select.Item key={s.id} value={s.id} className="form-select-item">
                            <Select.ItemText>{s.title}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                  {selectedSourceId && (
                    <Select.Root
                      value={addCitationId || undefined}
                      onValueChange={(v) => setAddCitationId(v || '')}
                      disabled={loadingCitations || submitting}
                    >
                      <Select.Trigger className="form-select-trigger" aria-label={t('citations.selectCitationAria')}>
                        <Select.Value placeholder={t('citations.selectCitationPlaceholder')} />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                          {availableCitations.map((c) => (
                            <Select.Item key={c.id} value={c.id} className="form-select-item">
                              <Select.ItemText>{citationLabel(c)}</Select.ItemText>
                            </Select.Item>
                          ))}
                          <Select.Item value={CREATE_NEW_VALUE} className="form-select-item">
                            <Select.ItemText>{t('citations.createNewCitation')}</Select.ItemText>
                          </Select.Item>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  )}
                </div>
                {showCreateForm && (
                  <form className="citation-links-field__create-form" onSubmit={handleCreateAndLinkCitation}>
                    <div className="citation-links-field__create-row">
                      <label className="citation-links-field__create-label">{t('citations.detailOptional')}</label>
                      <input
                        type="text"
                        className="citation-links-field__create-input"
                        value={newDetailLocator}
                        onChange={(e) => setNewDetailLocator(e.target.value)}
                        placeholder={t('citations.detailPlaceholder')}
                      />
                    </div>
                    <div className="citation-links-field__create-row">
                      <label className="citation-links-field__create-label">{t('citations.notesOptional')}</label>
                      <input
                        type="text"
                        className="citation-links-field__create-input"
                        value={newNotes}
                        onChange={(e) => setNewNotes(e.target.value)}
                        placeholder={t('citations.notesPlaceholder')}
                      />
                    </div>
                    <div className="citation-links-field__create-row">
                      <label className="citation-links-field__create-label">{t('citations.confidenceOptional')}</label>
                      <Select.Root
                        value={newConfidence || undefined}
                        onValueChange={(v) => setNewConfidence((v as (typeof CONFIDENCE_OPTIONS)[number]) || '')}
                      >
                        <Select.Trigger className="form-select-trigger" aria-label={t('citations.confidence')}>
                          <Select.Value placeholder={tCommon('emptyDefault')} />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                            {CONFIDENCE_OPTIONS.map((c) => (
                              <Select.Item key={c} value={c} className="form-select-item">
                                <Select.ItemText>{t(`citations.confidenceLevels.${c}`)}</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>
                    <button type="submit" className="citation-links-field__add-button" disabled={submitting}>
                      {submitting ? t('citations.creating') : t('citations.createAndLink')}
                    </button>
                  </form>
                )}
                {!showCreateForm && addCitationId.trim() && addCitationId !== CREATE_NEW_VALUE && (
                  <form className="citation-links-field__link-form" onSubmit={handleLinkExistingCitation}>
                    <button type="submit" className="citation-links-field__add-button" disabled={submitting}>
                      {submitting ? t('citations.linking') : t('citations.linkCitation')}
                    </button>
                  </form>
                )}
              </div>
            )}
          </>
        )}
        {familyId && imageOverlayMediaId && (
          <ImageEnlargeOverlay
            isOpen
            onClose={() => setImageOverlayMediaId(null)}
            familyId={familyId}
            mediaAssetId={imageOverlayMediaId}
          />
        )}
      </div>
    );
  }
);

export default CitationLinksField;
